"use client";

import { useParams } from "next/navigation";
import { useHydratedSearchParams } from "@/hooks/use-hydrated-search-params";
import { useMemo } from "react";
import AppDetailView from "@/components/app-detail-view";
import { parseAppIdFromSlug } from "@/lib/utils";
import { useThemeConfig } from "@/hooks/use-theme-config";
import type { NormalizedAppData } from "@/lib/app-data-helpers";
import { Activity, Cpu, Database, Radio } from "lucide-react";

export default function AppDetailSlugClient({ initialCore, dataUpdatedAt }: { initialCore?: Partial<NormalizedAppData>; dataUpdatedAt?: number }) {
  const search = useHydratedSearchParams();
  const params = useParams();
  const slug = params?.slug as string;
  const country = search.get("country") || undefined;
  const category = search.get("category") || undefined;
  const from = country ? `/${country}${category ? `/${category}` : ''}` : "/";
  const date = search.get("date") || undefined;

  const appId = useMemo(() => (slug ? parseAppIdFromSlug(slug) : null), [slug]);

  // Hydration-safe pattern: `mounted` from useThemeConfig is false during SSR
  // and becomes true after the first client render.
  const { isTerminal, mounted } = useThemeConfig();

  const showTerminal = mounted && isTerminal;

  if (!appId) {
    return (
      <div className="p-4">
        <p className="text-sm text-muted-foreground">Invalid app slug.</p>
      </div>
    );
  }

  // TERMINAL MODE - Bloomberg-style wrapper (static, no animations)
  if (showTerminal) {
    return (
      <div className="relative min-h-screen bg-[var(--ast-brand-night)] overflow-hidden">
        {/* Static gradient glow */}
        <div
          className="absolute inset-0 pointer-events-none opacity-60"
          style={{
            background: "radial-gradient(ellipse at center, rgba(var(--ast-brand-green-rgb),0.14) 0%, transparent 70%)",
          }}
        />

        {/* Scanlines overlay */}
        <div
          className="fixed inset-0 pointer-events-none z-[100] opacity-[0.03]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0, 0, 0, 0.3) 2px, rgba(0, 0, 0, 0.3) 4px)",
          }}
        />

        {/* Static corner accents */}
        <div
          className="fixed top-0 left-0 w-16 h-16 border-t-2 border-l-2 border-[var(--ast-brand-green)] z-[101]"
          style={{ boxShadow: "0 0 15px rgba(var(--ast-brand-green-rgb),0.4)" }}
        />
        <div
          className="fixed top-0 right-0 w-16 h-16 border-t-2 border-r-2 border-[var(--ast-brand-green)] z-[101]"
          style={{ boxShadow: "0 0 15px rgba(var(--ast-brand-green-rgb),0.4)" }}
        />
        <div
          className="fixed bottom-0 left-0 w-16 h-16 border-b-2 border-l-2 border-[var(--ast-brand-green)] z-[101]"
          style={{ boxShadow: "0 0 15px rgba(var(--ast-brand-green-rgb),0.4)" }}
        />
        <div
          className="fixed bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 border-[var(--ast-brand-green)] z-[101]"
          style={{ boxShadow: "0 0 15px rgba(var(--ast-brand-green-rgb),0.4)" }}
        />

        {/* Static background glows */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div
            className="absolute -top-32 -left-32 w-96 h-96 bg-[var(--ast-brand-green)] rounded-full blur-[150px] opacity-12"
          />
          <div
            className="absolute -bottom-32 -right-32 w-80 h-80 bg-[var(--ast-brand-forest)] rounded-full blur-[120px] opacity-10"
          />
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[var(--ast-brand-green)] rounded-full blur-[200px] opacity-10"
          />
        </div>

        {/* Terminal header bar */}
        <div className="sticky top-0 z-50 flex items-center justify-between px-4 py-2 border-b border-[color:rgba(var(--ast-brand-green-rgb),0.3)] bg-[rgba(var(--ast-brand-night-rgb),0.92)] backdrop-blur-md">
          <div className="flex items-center gap-4">
            {/* Window chrome dots - static */}
            <div className="flex gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-[var(--ast-brand-green)] shadow-[0_0_6px_rgba(var(--ast-brand-green-rgb),0.7)]" />
              <div className="w-2.5 h-2.5 rounded-full bg-[rgba(var(--ast-brand-forest-rgb),0.7)]" />
              <div className="w-2.5 h-2.5 rounded-full bg-[rgba(var(--ast-brand-moss-rgb),0.45)]" />
            </div>

            {/* Terminal identifier */}
            <div className="flex items-center gap-2">
              <Cpu className="h-3.5 w-3.5 text-[var(--ast-brand-green)]" />
              <span className="font-mono text-[11px] text-[rgba(var(--ast-brand-green-rgb),0.78)] uppercase tracking-[0.25em]">
                APP.DETAIL
              </span>
              <span className="font-mono text-[11px] text-[rgba(var(--ast-brand-green-rgb),0.45)]">|</span>
              <span className="font-mono text-[11px] text-[var(--ast-brand-text)] uppercase tracking-wider truncate max-w-[200px]">
                ID:{appId}
              </span>
            </div>
          </div>

          {/* Status indicators - static */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <Database className="h-3 w-3 text-[rgba(var(--ast-brand-moss-rgb),0.75)]" />
              <span className="font-mono text-[9px] text-[rgba(var(--ast-brand-moss-rgb),0.75)] uppercase tracking-wider">
                DB.SYNC
              </span>
            </div>

            <div className="flex items-center gap-1.5">
              <Activity className="h-3 w-3 text-[rgba(var(--ast-brand-green-rgb),0.8)]" />
              <span className="font-mono text-[9px] text-[rgba(var(--ast-brand-green-rgb),0.8)] uppercase tracking-wider">
                ACTIVE
              </span>
            </div>

            {/* LIVE badge - static */}
            <div
              className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-[rgba(var(--ast-brand-green-rgb),0.12)] border border-[color:rgba(var(--ast-brand-green-rgb),0.45)]"
              style={{ boxShadow: "0 0 10px rgba(var(--ast-brand-green-rgb),0.22)" }}
            >
              <span className="relative flex h-2 w-2">
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[var(--ast-brand-green)] shadow-[0_0_8px_rgba(var(--ast-brand-green-rgb),0.7)]" />
              </span>
              <span className="font-mono text-[10px] text-[var(--ast-brand-green)] uppercase tracking-widest font-bold">
                LIVE
              </span>
            </div>
          </div>
        </div>

        {/* Main content with static emerald border glow */}
        <div
          className="relative mx-2 my-2 border border-[color:rgba(var(--ast-brand-green-rgb),0.35)] rounded-sm overflow-hidden"
          style={{
            boxShadow: "0 0 30px rgba(var(--ast-brand-green-rgb),0.12), inset 0 0 40px rgba(var(--ast-brand-green-rgb),0.04)",
          }}
        >
          {/* Inner scanlines for content area */}
          <div
            className="absolute inset-0 pointer-events-none z-10 opacity-[0.02]"
            style={{
              backgroundImage:
                "repeating-linear-gradient(0deg, transparent, transparent 1px, rgba(var(--ast-brand-green-rgb),0.08) 1px, rgba(var(--ast-brand-green-rgb),0.08) 2px)",
            }}
          />

          <div className="flex flex-col h-[calc(100vh-60px)] overflow-hidden bg-[rgba(var(--ast-brand-night-rgb),0.55)]">
            <div className="flex-1 overflow-auto pb-10">
              <AppDetailView appId={appId} initialData={initialCore} dataUpdatedAt={dataUpdatedAt} />
            </div>
          </div>
        </div>

        {/* Bottom status bar - static */}
        <div className="fixed bottom-0 left-0 right-0 z-50 flex items-center justify-between px-4 py-1.5 border-t border-[color:rgba(var(--ast-brand-green-rgb),0.2)] bg-[rgba(var(--ast-brand-night-rgb),0.96)] backdrop-blur-md">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <Radio className="h-3 w-3 text-[rgba(var(--ast-brand-forest-rgb),0.7)]" />
              <span className="font-mono text-[9px] text-[rgba(var(--ast-brand-forest-rgb),0.7)] uppercase tracking-wider">
                RT.FEED
              </span>
            </div>
            <span className="font-mono text-[9px] text-[rgba(var(--ast-brand-green-rgb),0.28)]">|</span>
            <span className="font-mono text-[9px] text-[rgba(var(--ast-brand-forest-rgb),0.6)] uppercase tracking-wider">
              SRC: APPSTORE.API
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-mono text-[9px] text-[rgba(var(--ast-brand-forest-rgb),0.6)] uppercase tracking-wider">
              {country ? `REGION: ${country.toUpperCase()}` : "REGION: GLOBAL"}
            </span>
            <span className="font-mono text-[9px] text-[rgba(var(--ast-brand-green-rgb),0.28)]">|</span>
            <span className="font-mono text-[9px] text-[rgba(var(--ast-brand-moss-rgb),0.78)] uppercase tracking-wider">
              APPSTORE.TERMINAL.v2
            </span>
          </div>
        </div>
      </div>
    );
  }

  // LIGHT/DARK MODE - Original clean styling
  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex-1 overflow-auto">
        <AppDetailView appId={appId} initialData={initialCore} dataUpdatedAt={dataUpdatedAt} />
      </div>
    </div>
  );
}

